/**
 * The menu as a page to print (docs/01-architektur.md, Ausgaben und Vorlagen): A4, in one of three
 * layouts. Klassisch: name and price, the description below. Kompakt: one line per dish. Zweispaltig:
 * klassisch in two columns. The same page is the preview in the app. Allergens and additives stand
 * as codes by the name; the legend at the bottom names only those in use. The business's logo (S13)
 * stands centred above its name, or instead of it.
 */
import { DEFAULT_DESIGN, FOOT_LOGO_SIZE, LOGO_SIZE, MIN_COLUMN, cardWords, coursesOf, cssFamily, drinkEuroOf, cssVariant, mm, pageOf, designFiles, fitLogo, fontFaceOf, langParts, legendLines, showsHouse, showsPrices } from "@speisekarte/core";
import type { Design, FootLogoPlace, Lang, Logo, LogoPlace, MenuOwnPages, MenuView, StyleKey, TextStyle, ViewItem, ViewSection } from "@speisekarte/core";

export interface PrintMenu {
  view: MenuView;
  house: string;
  date: Date;
  /** „Alle Preise in Euro inkl. MwSt.“; empty: the default. */
  footer: string;
  /** The logo, when the device has it (logo.ts). */
  logo?: Logo | null;
  /** The logo in the foot of the pages (S43), when the style has it there and the device has it. */
  footLogo?: Logo | null;
  /** „Logo statt Name“. */
  logoOnly?: boolean;
  /** Above the head (default), or at its left or right. */
  logoPlace?: LogoPlace;
  /** The business's style (S15); without one „Klassisch“. */
  design?: Design | null;
  /** Which style it is (S21): written into the Word file, so an uploaded one finds its style. */
  style?: { id: string; name: string };
  /** An insert (S37, L3): the small head, the logo in half its box. */
  small?: true;
  /** The list at the entrance (S42): what it shows, noted when printed. */
  entrance?: string;
  /** The house's website, Instagram and Facebook in one line at the foot (S50), when it wants them in print. */
  links?: string;
  /** The own pages the menu takes (S50), by id; their files in `own` once fetched (output.ts). Never for an insert. */
  ownPages?: MenuOwnPages;
  own?: { front: Uint8Array[]; back: Uint8Array[] };
}

/** The box of the logo on paper, in points (about 70 × 24 mm): the same in print, PDF and Word. */
export const LOGO_BOX = { width: 198, height: 68 };
/** Beside the head: about 46 × 30 mm. */
export const SIDE_LOGO_BOX = { width: 130, height: 85 };

/** How much of each size of the head an insert keeps (S37, L3); the logo half its box (`logoLayout`). */
export const SMALL_HEAD = { house: 0.55, title: 0.85, lead: 0.9 };

/** The small head of an insert (S37: large on the main menu, small on the insert): name, title and subtitle smaller. */
export function smallHead(d: Design): Design {
  const by = (k: "house" | "title" | "lead", f: number) => ({ ...d.styles[k], size: Math.round(d.styles[k].size * f * 10) / 10 });
  return { ...d, styles: { ...d.styles, house: by("house", SMALL_HEAD.house), title: by("title", SMALL_HEAD.title), lead: by("lead", SMALL_HEAD.lead) } };
}

/**
 * Where the logo stands on this menu (null: above, centred) and its box: at the style's size (S13),
 * never wider than the page has room for (at the side, half of it); half of that on an insert.
 */
export function logoLayout(m: PrintMenu): { side: "left" | "right" | null; box: { width: number; height: number } } {
  const side = m.logo && m.logoPlace && m.logoPlace !== "center" ? m.logoPlace : null;
  const d = m.design ?? DEFAULT_DESIGN;
  const f = (d.logoSize ?? LOGO_SIZE.fallback) / 100;
  const room = (pageOf(d.page.format).width - 2 * mm(d.page.marginX)) * (side ? 0.5 : 1);
  const base = side ? SIDE_LOGO_BOX : LOGO_BOX;
  const box = { width: Math.min(base.width * f, room), height: base.height * f };
  return { side, box: m.small ? { width: box.width / 2, height: box.height / 2 } : box };
}

/** The box of the logo in the foot (S43), in points: about 40 × 14 mm at 100 %. */
export const FOOT_LOGO_BOX = { width: 113, height: 40 };

/**
 * The logo in the foot of the pages (S43): where, its size, and on which pages – each without the
 * logo in the head, so page 1 only when the head has none. Null: no logo in the foot.
 */
export function footLogoLayout(m: PrintMenu): { place: FootLogoPlace; width: number; height: number; onFirst: boolean } | null {
  const d = m.design ?? DEFAULT_DESIGN;
  if (!m.footLogo || !d.footLogo) return null;
  const f = (d.footLogoSize ?? FOOT_LOGO_SIZE.fallback) / 100;
  const room = pageOf(d.page.format).width - 2 * mm(d.page.marginX);
  const size = fitLogo(m.footLogo, Math.min(FOOT_LOGO_BOX.width * f, room), FOOT_LOGO_BOX.height * f);
  return { place: d.footLogo, ...size, onFirst: !m.logo };
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
/** The further language of a bilingual card while its page is made (S36), else null. */
let pair: Lang | null = null;
/**
 * A short text of the card: on a bilingual one „Vorspeisen / Başlangıçlar“ with the part after „ / “
 * marked as its language, so the browser writes its capitals by it (docs/21-tuerkisch.md § 3.3).
 */
const say = (s: string) => (pair ? langParts(s, "de", pair).map((p, i) => (i ? `<span lang="${p.lang}">${esc(p.text)}</span>` : esc(p.text))).join("") : esc(s));
/** „5,90“: in a table without the euro sign, so the commas stand one below the other (as KellerLog). */
export const price = (cents: number | null) => (cents === null ? "" : (cents / 100).toFixed(2).replace(".", ","));
export const stand = (d: Date) => d.toLocaleDateString("de-DE");
/** „Alle Preise in Euro inkl. MwSt. · Stand …“; empty on a menu without prices. */
export function footerLine(m: PrintMenu): string {
  if (!showsPrices(m.view)) return "";
  // In a further language (S36) the default and „Stand“ in it; the business's own text as it wrote it.
  const words = cardWords(m.view);
  return `${m.footer || words.footer} · ${words.asOf} ${stand(m.date)}`;
}

const CSS = `
@page { size: A4; margin: 16mm 16mm 18mm; }
* { box-sizing: border-box; }
html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { margin: 0; color: #2a2522; font: 10.5pt/1.38 "Iowan Old Style", "Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif; }
@media screen { body { background: #fff; padding: 12mm 10mm; max-width: 210mm; margin: 0 auto; } }
header { text-align: center; margin-bottom: 7mm; }
header .logo { display: block; margin: 0 auto 3mm; }
header.side { display: flex; align-items: center; gap: 7mm; text-align: left; }
header.side.right { flex-direction: row-reverse; }
header.side .logo { margin: 0; flex: none; }
header.side .head { flex: 1; min-width: 0; }
.house { font-size: 24pt; font-variant: small-caps; letter-spacing: 0.06em; line-height: 1.1; }
.title { font: 8.5pt system-ui, -apple-system, sans-serif; letter-spacing: 0.25em; text-transform: uppercase; color: #8a7d74; margin-top: 2mm; }
.lead { font-style: italic; color: #8a7d74; margin: 1.5mm 0 0; }
.unit { break-inside: avoid; }
h2 { display: flex; justify-content: space-between; align-items: baseline; gap: 4mm; font-size: 15pt; font-weight: normal; font-variant: small-caps; letter-spacing: 0.04em; color: #a2461a; margin: 6mm 0 1.5mm; padding-bottom: 1mm; border-bottom: 0.3mm solid #e2d4c6; break-after: avoid; }
h2 .p { font-variant: normal; font-size: 12pt; color: #2a2522; font-variant-numeric: tabular-nums; }
.snote { font-style: italic; color: #8a7d74; font-size: 9pt; margin: 0 0 1.5mm; break-after: avoid; }
.item { display: grid; grid-template-columns: var(--no) minmax(0, 1fr) auto; column-gap: 3mm; padding: 1.4mm 0; break-inside: avoid; }
body.unnumbered { --no: 0; }
body { --no: 7mm; }
body.unnumbered .item { column-gap: 0; }
body.unnumbered .item .pr { padding-left: 3mm; }
.no { color: #a2461a; text-align: center; font-variant-numeric: tabular-nums; font-size: 9.5pt; }
.nm b { font-weight: 600; }
sup { font: 6.5pt system-ui, -apple-system, sans-serif; color: #8a7d74; margin-left: 0.6mm; }
.tags { font: 6.5pt system-ui, -apple-system, sans-serif; color: #a2461a; letter-spacing: 0.04em; margin-left: 1.5mm; }
.d { margin: 0.4mm 0 0; font-style: italic; font-size: 9.2pt; color: #5c524b; }
.xtext { margin: 3mm 0 1mm; break-inside: avoid; }
.xtext .tt { font-style: italic; font-size: 12.5pt; color: #a2461a; }
.xtext .tl { font-weight: 600; font-size: 11pt; }
.xtext .tb { margin: 0.5mm 0 0; }
.n { margin: 0.3mm 0 0; font: 7.5pt system-ui, -apple-system, sans-serif; color: #a2461a; }
.pr { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
body.compact .item { padding: 0.7mm 0; }
body.compact .d { display: none; }
body.compact .nm { display: flex; align-items: baseline; gap: 1.5mm; }
body.compact .nm::after { content: ""; flex: 1; border-bottom: 0.25mm dotted #c9b8ab; transform: translateY(-0.8mm); }
body.columns main { column-count: 2; column-gap: 9mm; }
body.columns h2:first-child { margin-top: 0; }
.course { break-inside: avoid; }
.course .item.alt { padding-bottom: 0.5mm; }
.course .item.ao, .gc + .item { padding-top: 0.5mm; }
.or, .gc { padding-left: calc(var(--no) + 3mm); }
body.unnumbered .or, body.unnumbered .gc { padding-left: 0; }
.or { font-style: italic; font-size: 9.2pt; color: #5c524b; }
.gc { font-variant: small-caps; letter-spacing: 0.04em; color: #a2461a; padding-top: 1.4mm; }
footer { margin-top: 8mm; padding-top: 3mm; border-top: 0.3mm solid #e2d4c6; font: 7.5pt/1.45 system-ui, -apple-system, sans-serif; color: #8a7d74; break-inside: avoid; }
footer p { margin: 0 0 1.2mm; }
footer .note { font: italic 9pt/1.4 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #5c524b; text-align: center; margin-bottom: 2.5mm; }
footer .stand { text-align: center; margin-top: 2mm; }
`;

/** Where each style goes on the page. */
const SELECTORS: Record<StyleKey, string> = {
  house: ".house",
  title: ".title",
  lead: ".lead",
  section: "h2",
  subsection: ".us",
  sectionNote: ".snote",
  number: ".no",
  dish: ".nm b",
  description: ".d",
  variant: ".vl",
  price: ".pr, h2 .p",
  codes: "sup",
  tags: ".tags",
  itemNote: ".n",
  textTitle: ".xtext .tt",
  text: ".xtext .tb",
  textLine: ".xtext .tl",
  course: ".gc",
  orLine: ".or",
  part: "h1.part",
  sizeHead: ".colhead",
  menuNote: "footer .note",
  legend: "footer",
};

const rule = (s: TextStyle) =>
  [
    `font-family: ${cssFamily(s)}`,
    `font-size: ${s.size}pt`,
    `color: ${s.color}`,
    `font-weight: ${s.bold ? 700 : 400}`,
    `font-style: ${s.italic ? "italic" : "normal"}`,
    `text-transform: ${s.caps === "upper" ? "uppercase" : "none"}`,
    `font-variant: ${cssVariant(s)}`,
    `letter-spacing: ${s.tracking / 100}em`,
  ].join("; ");

/** The style (S15) as rules after the page's own: fonts from the app, sizes, colours, spaces. */
export function designCss(d: Design, origin = typeof location === "undefined" ? "" : location.origin): string {
  const faces = designFiles(d).map((file) => {
    const { family, weight, style } = fontFaceOf(file);
    return `@font-face { font-family: "${family}"; font-weight: ${weight}; font-style: ${style}; src: url("${origin}/fonts/${file}") format("woff"); }`;
  });
  const styles = (Object.keys(SELECTORS) as StyleKey[]).map((k) => `${SELECTORS[k]} { ${rule(d.styles[k])} }`);
  const paper = pageOf(d.page.format);
  const size = paper.id === "dinlang" ? "99mm 210mm" : paper.label;
  return [
    ...faces,
    ...styles,
    ".xtext .tl { font-weight: 700; }",
    // The translation on a bilingual card (S36): the other way round from the description, italic or upright.
    `.d.tr { font-style: ${d.styles.description.italic ? "normal" : "italic"}; }`,
    "h2 .p { font-size: 0.8em; }",
    `header:not(.side) { text-align: ${d.headAlign}; }`,
    d.headAlign === "left" ? "header:not(.side) .logo { margin-left: 0; }" : "",
    `h2 { margin-top: ${d.sectionSpace}pt; border-bottom: ${d.rule ? "0.3mm solid #e2d4c6" : "none"}; }`,
    `.item { padding: ${d.dishSpace / 2}pt 0; grid-template-columns: var(--no) minmax(0, 1fr) ${d.columns.price}pt; }`,
    `body { --no: ${d.columns.number}pt; }`,
    euroCss(d),
    `@page { size: ${size}; margin: ${d.page.marginY}mm ${d.page.marginX}mm ${d.page.marginY + 4}mm; }`,
    (paper.width - 2 * mm(d.page.marginX) - 26) / 2 < MIN_COLUMN ? "body.columns main { column-count: 1; }" : "",
    `@media screen { body { max-width: ${(paper.width * 25.4) / 72}mm; padding: ${d.page.marginY}mm ${d.page.marginX}mm; } }`,
    `body.compact .item { padding: ${d.dishSpace * 0.28}pt 0; }`,
  ].join("\n");
}

/** „€“ after the prices: the drinks' sections follow their own choice (drinkEuroOf). */
function euroCss(d: Design): string {
  const after = (scope: string) => `${scope}.item .pr:not(:empty)::after, ${scope}h2 .p::after { content: " €"; }`;
  const food = d.euro;
  const drinks = drinkEuroOf(d);
  if (food === drinks) return food ? after("") : "";
  return food ? after("section:not(.drinks) ") : after("section.drinks ");
}

/** A text (S14): a title in the accent with its text below, or a title alone in bold. */
function textItem(i: ViewItem): string {
  const title = i.name ? `<div class="${i.description ? "tt" : "tl"}">${esc(i.name)}</div>` : "";
  return `<div class="xtext">${title}${i.description ? `<p class="tb">${esc(i.description)}</p>` : ""}</div>`;
}

function item(i: ViewItem, set: boolean, alt = false): string {
  if (i.text) return textItem(i);
  const codes = i.codes.length ? `<sup>${esc(i.codes.join(","))}</sup>` : "";
  const tags = i.tags.length ? `<span class="tags">${esc(i.tags.join(" · "))}</span>` : "";
  // In a set menu only a surcharge (S32): „+ 8,00“.
  const pr = set ? (i.surcharge ? `+ ${price(i.surcharge)}` : "") : price(i.price);
  return `<div class="item${alt ? " alt" : ""}${i.or ? " ao" : ""}"><span class="no">${i.number ?? ""}</span><div><div class="nm"><span><b>${esc(i.name)}</b>${codes}${tags}</span></div>${i.description ? `<p class="d">${esc(i.description)}</p>` : ""}${i.trans ? `<p class="d tr">${esc(i.trans)}</p>` : ""}${i.note ? `<p class="n">${esc(i.note)}</p>` : ""}</div><span class="pr">${pr}</span></div>`;
}

/** A set menu's courses (S32), each kept together: its title, its dishes with „oder“ between them. */
function courses(items: ViewItem[], orText: string): string[] {
  return coursesOf(items).map((c) => {
    const title = c[0]!.course ? `<div class="gc">${say(c[0]!.course)}</div>` : "";
    return `<div class="course">${title}${c.map((i, k) => `${k ? `<div class="or">${esc(orText)}</div>` : ""}${item(i, true, k < c.length - 1)}`).join("")}</div>`;
  });
}

function section(s: ViewSection, orText: string): string {
  const set = s.kind === "set";
  if (set) {
    const head = `<h2><span>${say(s.title || "Menü")}</span>${s.price !== null ? `<span class="p">${price(s.price)}</span>` : ""}</h2>`;
    const note = s.note ? `<p class="snote">${say(s.note)}</p>` : "";
    const [first, ...rest] = courses(s.items, orText);
    return `<section>${first ? `<div class="unit">${head}${note}${first}</div>` : `${head}${note}`}${rest.join("")}</section>`;
  }
  const head = s.title || set ? `<h2><span>${say(s.title || "Menü")}</span>${set && s.price !== null ? `<span class="p">${price(s.price)}</span>` : ""}</h2>` : "";
  const note = s.note ? `<p class="snote">${say(s.note)}</p>` : "";
  // The heading goes with the first dish (never alone at the bottom of a page or column).
  const [first, ...rest] = s.items;
  return `<section${s.part === "drinks" ? ` class="drinks"` : ""}>${first ? `<div class="unit">${head}${note}${item(first, set)}</div>` : `${head}${note}`}${rest.map((i) => item(i, set)).join("")}</section>`;
}

/** The whole page, ready to print or to show in a frame. */
export function menuHtml(m: PrintMenu): string {
  const v = m.view;
  pair = v.lang?.both ? v.lang.lang : null;
  const { allergens, additives } = legendLines(v.legend);
  const words = cardWords(v);
  let body = v.sections.filter((s) => s.items.length > 0).map((s) => section(s, (m.design ?? DEFAULT_DESIGN).orText)).join("");
  const classes = [v.layout === "compact" ? "compact" : v.layout === "columns" ? "columns" : "", v.numbered ? "" : "unnumbered"].filter(Boolean).join(" ");
  const foot = [
    v.note ? `<p class="note">${say(v.note)}</p>` : "",
    allergens ? `<p><b>${esc(words.allergens)}:</b> ${esc(allergens)}</p>` : "",
    additives ? `<p><b>${esc(words.additives)}:</b> ${esc(additives)}</p>` : "",
    footerLine(m) ? `<p class="stand">${esc(footerLine(m))}</p>` : "",
    m.links ? `<p class="stand">${esc(m.links)}</p>` : "",
  ].join("");
  const { side, box } = logoLayout(m);
  const size = m.logo ? fitLogo(m.logo, box.width, box.height) : null;
  const logo = m.logo && size ? `<img class="logo" src="data:image/png;base64,${m.logo.data}" alt="${esc(m.house)}" style="width:${size.width.toFixed(1)}pt;height:${size.height.toFixed(1)}pt">` : "";
  const house = showsHouse(m.logo, m.logoOnly ?? false) ? `<div class="house">${esc(m.house)}</div>` : "";
  const text = `${house}<div class="title">${say(v.title)}</div>${v.subtitle ? `<p class="lead">${say(v.subtitle)}</p>` : ""}`;
  pair = null;
  const head = side ? `<header class="side ${side}">${logo}<div class="head">${text}</div></header>` : `<header>${logo}${text}</header>`;
  // On a bilingual card (S36) the translations marked as their language, so the browser hyphenates them by it.
  if (m.view.lang?.both) body = body.replaceAll('<p class="d tr">', `<p class="d tr" lang="${m.view.lang.lang}">`);
  return `<!doctype html><html lang="${m.view.lang && !m.view.lang.both ? m.view.lang.lang : "de"}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${esc(`${v.title} ${m.house}`)}</title><style>${CSS}${designCss(m.design ?? DEFAULT_DESIGN)}</style></head><body${classes ? ` class="${classes}"` : ""}>${head}<main>${body || `<p class="lead">Noch keine Speisen auf der Speisekarte.</p>`}</main><footer>${foot}</footer></body></html>`;
}

/** A file name from the title: „Tapas-Karte 2026-10-21“. */
export const fileName = (m: PrintMenu, ext: string) => `${`${m.view.title} ${m.date.toLocaleDateString("sv-SE")}`.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim()}.${ext}`;
